import auth from "../modules/auth";

const getDefaultState = () => {
  return {
    sessionID: "",
    dateTimeMS: 0,
    /* ID etc OR null if not partner */
    partnerMetadata: {},

    sessionChat: [],

    ready: false,
    sessionLoaded: false,

    isCompleted: false,

    completionModalOpen: false,
    sessionCompleteConfirmed: false,

    userCurrentChatText: "",
    sendingMessage: false,
    chatError: "",
  };
};

const state = getDefaultState();

const getters = {};
// Synchronous
const mutations = {
  RESET_SESSION(state) {
    // Merge rather than replace so we don't lose observers
    // https://github.com/vuejs/vuex/issues/1118
    Object.assign(state, getDefaultState());
  },
  RESET_SESSION_STATES(state) {
    state.ready = false;
    state.sessionLoaded = false;
    state.isCompleted = false;
  },
  SET_PARTNER_METADATA(state, newData) {
    state.partnerMetadata = newData;
  },
  SET_SESSION_ID(state, newID) {
    state.sessionID = newID;
  },
  SET_CURRENT_CHAT_TEXT(state, text) {
    state.userCurrentChatText = text;
  },
  SET_SENDING_MESSAGE_STATUS(state, status) {
    state.sendingMessage = status;
  },
  SET_SESSION_DATETIME(state, newDateMS) {
    state.dateTimeMS = newDateMS;
  },
  SET_SESSION_CHAT(state, chat) {
    state.sessionChat = chat;
  },
  SET_CHAT_ERROR(state, newStatus) {
    state.chatError = newStatus;
  },
  ADD_MESSAGE(state, message) {
    /* Prevent double messages visually */
    const index = state.sessionChat.findIndex((obj) => obj.dateMS === message.dateMS);
    if (index === -1) {
      state.sessionChat.push(message);
    }
  },
  REMOVE_MESSAGE(state, messageData) {
    if (!messageData || !messageData.dateMS || !messageData.SID || !messageData.UID) return;
    for (let i = 0; i < state.sessionChat.length; i++) {
      const message = state.sessionChat[i];
      if (message.dateMS === messageData.dateMS && message.SID === messageData.SID && message.UID == messageData.UID) {
        state.sessionChat.splice(i, 1);
        return;
      }
    }
  },
  SET_SESSION_COMPLETE(state, newValue) {
    state.isCompleted = newValue;
  },
  SET_SESSION_LOADED(state, newValue) {
    state.sessionLoaded = newValue;
  },
  SET_SESSION_READY(state, newValue) {
    state.ready = newValue;
  },

  SET_COMPLETION_MODAL_OPEN_STATE(state, newValue) {
    state.completionModalOpen = newValue;
  },
  SET_SESSION_COMPLETE_CONFIRMED_STATE(state, newValue) {
    state.sessionCompleteConfirmed = newValue;
  },

  SET_SESSION_DATA(state, sessionData) {
    state.isCompleted = sessionData.isCompleted;
    state.sessionID = sessionData.sessionID;
    state.dateTimeMS = sessionData.dateTimeMS;
    state.partnerMetadata = sessionData.partnerMetadata;
    if (sessionData.sessionChat) {
      state.sessionChat = sessionData.sessionChat;
    }
    state.sessionLoaded = sessionData.sessionLoaded;
    state.ready = sessionData.ready;
  },
};

// Asynchronous
const actions = {
  resetSession({ commit }) {
    commit("RESET_SESSION");
  },
  resetSessionStates({ commit }) {
    commit("RESET_SESSION_STATES");
  },
  setSessionComplete({ commit }, newValue) {
    commit("SET_SESSION_COMPLETE", newValue);
  },
  setSessionLoaded({ commit }, newValue) {
    commit("SET_SESSION_LOADED", newValue);
  },
  setSessionReady({ commit }, newValue) {
    commit("SET_SESSION_READY", newValue);
  },
  setChatError({commit}, newStatus) {
    commit("SET_CHAT_ERROR", newStatus);
  },
  /**
   * The message object contains the message data and
   * @param {Boolean} received the boolean indicating whether the message was received from the other person
   */
  addMessage({ state, commit, rootGetters, dispatch }, message) {
    console.log("@session.addMessage the message: ", message);
    /* If user sent the message, clear their message */
    if (message.UID === auth.state.user._id) {
      commit("SET_CURRENT_CHAT_TEXT", "");
    }
    if (message.SID === state.sessionID) {
      commit("ADD_MESSAGE", message);
    }
    if (message.received && !rootGetters["toolbar/roomChatOpen"]) {
      dispatch("toolbar/toggleChatIndicator", true, { root: true });
    }
  },
  removeMessage({ commit }, messageData) {
    commit("REMOVE_MESSAGE", messageData);
    /* Ensuring it's user's own message that is failing */
    if (messageData.UID === auth.state.user._id) {
      commit("SET_CURRENT_CHAT_TEXT", messageData.msg);
    }
  },
  setCurrentChatText({ commit }, text) {
    commit("SET_CURRENT_CHAT_TEXT", text);
  },
  setSendingMessageStatus({ commit }, status) {
    commit("SET_SENDING_MESSAGE_STATUS", status);
  },
  addSessionChat({ state, commit }, messages) {
    console.log("ADDING A WHOLE CHAT: ", messages);
    const newChat = state.sessionChat.concat(messages);
    newChat.sort((a, b) => a.dateMS - b.dateMS);
    /* eliminate duplicates */
    for (let i = newChat.length; --i >= 1; ) {
      const m1 = newChat[i];
      const m2 = newChat[i - 1];
      if (m1.dateMS === m2.dateMS && m1.UID === m2.UID && m1.msg === m2.msg) {
        newChat.splice(i, 0);
      }
    }
    commit("SET_SESSION_CHAT", newChat);
  },
  /**
   * Received data contains these elements
   * @param {Number} data.dateTimeMS the datetime of the match in ms
   * @param {String} data.SID the session ID
   * @param {Object} data.partner the user with whom the user got matched.
   */
  changeMatch({ commit }, payload) {
    const { dateTimeMS, SID, partner } = payload;
    if (partner) {
      commit("SET_PARTNER_METADATA", partner.metadata);
    } else {
      commit("SET_PARTNER_METADATA", null);
    }
    commit("SET_SESSION_ID", SID);
    commit("SET_SESSION_DATETIME", dateTimeMS);
    commit("SET_SESSION_CHAT", []);
  },

  setCompletionModalOpenState({ commit }, newValue) {
    commit("SET_COMPLETION_MODAL_OPEN_STATE", newValue);
  },

  setSessionCompleteConfirmedState({ commit }, newValue) {
    commit("SET_SESSION_COMPLETE_CONFIRMED_STATE", newValue);
  },

  setSessionData({ commit }, sessionData) {
    commit("SET_SESSION_DATA", sessionData);
  },

  setNewSessionID({ commit }, newSessionID) {
    commit("SET_SESSION_ID", newSessionID);
  },
};

const session = {
  namespaced: true,

  state,
  getters,
  mutations,
  actions,
};

export default session;
